import React from 'react';
import Icon from '../../../components/AppIcon';

const CategorySummaryCards = ({ expenses }) => {
  const categories = [
    { key: 'medical', label: 'Medical', icon: 'Heart', color: 'text-red-600', essential: true },
    { key: 'travel', label: 'Travel', icon: 'Plane', color: 'text-blue-600', essential: false },
    { key: 'personal', label: 'Personal', icon: 'User', color: 'text-purple-600', essential: false },
    { key: 'home', label: 'Home', icon: 'Home', color: 'text-green-600', essential: true },
    { key: 'food', label: 'Food', icon: 'Utensils', color: 'text-orange-600', essential: true },
    { key: 'transport', label: 'Transport', icon: 'Car', color: 'text-indigo-600', essential: true },
    { key: 'kids', label: 'Kids', icon: 'Baby', color: 'text-pink-600', essential: true },
    { key: 'parents', label: 'Parents', icon: 'Users', color: 'text-teal-600', essential: true }
  ];

  const calculateCategoryTotal = (categoryKey) => {
    return expenses
      .filter(expense => expense.category === categoryKey)
      .reduce((total, expense) => {
        const multiplier = {
          'monthly': 12,
          'quarterly': 4,
          'annually': 1,
          'one-time': 1
        }[expense.frequency] || 1;
        return total + (expense.amount * multiplier);
      }, 0);
  };

  const calculateInflationAdjusted = (categoryKey) => {
    return expenses
      .filter(expense => expense.category === categoryKey)
      .reduce((total, expense) => {
        const multiplier = {
          'monthly': 12,
          'quarterly': 4,
          'annually': 1,
          'one-time': 1
        }[expense.frequency] || 1;
        const years = expense.endYear - expense.startYear + 1;
        const inflationFactor = Math.pow(1 + expense.inflationRate / 100, years / 2);
        return total + (expense.amount * multiplier * inflationFactor);
      }, 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6 sm:mb-8">
      {categories.map((category) => {
        const currentTotal = calculateCategoryTotal(category.key);
        const inflationAdjusted = calculateInflationAdjusted(category.key);
        const expenseCount = expenses.filter(e => e.category === category.key).length;

        return (
          <div key={category.key} className="bg-card border border-border rounded-lg p-4 hover:shadow-soft transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-1.5 rounded-lg bg-muted ${category.color}`}>
                <Icon name={category.icon} size={18} />
              </div>
              <span className="text-sm text-muted-foreground">{expenseCount} items</span>
            </div>
            
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-medium text-foreground">{category.label}</h3>
              {category.essential && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-success/10 text-success border border-success/20">
                  <Icon name="Shield" size={12} /> Essential
                </span>
              )}
            </div>
            
            <div className="space-y-2">
              <div>
                <p className="text-sm text-muted-foreground">Current Annual</p>
                <p className="text-base font-semibold text-foreground">{formatCurrency(currentTotal)}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Inflation Adjusted</p>
                <p className="text-xs font-medium text-accent">{formatCurrency(inflationAdjusted)}</p>
              </div>
            </div>

            {category.key === 'travel' && (
              <div className="mt-3 p-2 bg-warning/10 rounded text-xs text-warning">
                <Icon name="Info" size={12} className="inline mr-1" />
                Reduces after age 75
              </div>
            )}

            {category.key === 'kids' && (
              <div className="mt-3 p-2 bg-accent/10 rounded text-xs text-accent">
                <Icon name="Info" size={12} className="inline mr-1" />
                Education costs end
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CategorySummaryCards;